import { PDFDocument, PDFFont, rgb } from 'pdf-lib';

import getConstants from '@/shared/constants';
import fontkit from '@pdf-lib/fontkit';

const { appBasePath } = getConstants();

type SignInfo = {
  signDate: string;
  thumbprint: string;
  issuer: string;
  owner: string;
  valid: string;
  [key: string]: string;
};

type FontMap = { [fontName: string]: PDFFont };

class UsePdfLib {
  pdfDoc: PDFDocument | null = null;
  isFontkitRegistered = false;
  fonts: FontMap = {};

  async open(src: ArrayBuffer | string): Promise<void> {
    let existingPdfBytes = src;
    if (typeof src === 'string') {
      existingPdfBytes = await fetch(src).then(res => res.arrayBuffer());
    }
    this.pdfDoc = await PDFDocument.load(existingPdfBytes);
  }

  async close(): Promise<Uint8Array> {
    if (!this.pdfDoc) throw new Error('PDF not loaded');
    const pdfDoc = await this.pdfDoc.save();
    this.pdfDoc = null;
    return pdfDoc;
  }

  async loadFont(fontName: string, fontUrl: string): Promise<PDFFont> {
    if (!this.pdfDoc) throw new Error('PDF not loaded');
    if (!this.isFontkitRegistered) {
      this.pdfDoc.registerFontkit(fontkit as any);
      this.isFontkitRegistered = true;
    }
    if (!(fontName in this.fonts)) {
      const fontBytes = await fetch(fontUrl).then(res => res.arrayBuffer());
      this.fonts[fontName] = await this.pdfDoc.embedFont(fontBytes);
    }
    return this.fonts[fontName];
  }

  async drawStamp(info: SignInfo, startX: number): Promise<void> {
    if (!this.pdfDoc) throw new Error('PDF not loaded');
    const customFont = await this.loadFont(
      'ubuntu-r',
      `${appBasePath}Ubuntu-R.ttf`
    );

    function splitter(
      arr: { text: string; value?: string }[],
      maxLength = 37,
      minTailLength = 4
    ) {
      const result: { text: string; value?: string }[] = [];
      for (const el of arr) {
        let text = el.text;
        let left = el.value;
        if (!el.value || text === 'Сертификат:') {
          result.push(el);
          continue;
        }
        let right: string;
        do {
          right = left.slice(maxLength);
          left = left.slice(0, maxLength);
          if (right.length > 0 && right.length < minTailLength) {
            left = left + right;
            right = '';
          }
          result.push({ text, value: left });
          left = right;
          text = '';
        } while (right);
      }
      return result;
    }

    const textData = splitter([
      { text: 'Документ подписан электронной подписью' },
      { text: 'Дата и время:', value: info.signDate },
      { text: 'Сертификат:', value: info.thumbprint },
      { text: 'Кем выдан:', value: info.issuer },
      { text: 'Владелец:', value: info.owner },
      { text: 'Действителен:', value: info.valid },
    ]);

    let texts = '';
    textData.forEach(item => {
      if ('value' in item) {
        return (texts += `${item.text}\t${item.value} \n`);
      }
      texts += `${item.text} \n\n`;
    });

    const pages = this.pdfDoc.getPages();
    const stampColor = {
      border: rgb(0, 0, 1),
      title: rgb(0.1, 0.1, 0.95),
      value: rgb(0.1, 0.1, 0.95),
    };

    const textSize = 4;
    const deltaY = 5;
    const startY = (textData.length + 5) * deltaY;
    const x = startX + 8;
    const boxHeight = (textData.length + 3) * textSize;

    for (const page of pages) {
      page.drawText(texts, {
        x,
        y: startY,
        size: textSize,
        font: customFont,
        lineHeight: 4,
        color: stampColor.title,
      });

      page.drawRectangle({
        x: startX,
        y: 6 * deltaY,
        width: 140,
        height: boxHeight,
        borderColor: stampColor.border,
        borderWidth: 1.5,
      });
    }
  }
}

export async function applySignInfo(
  existingPdfBytes: ArrayBuffer | Uint8Array,
  info: SignInfo
): Promise<Uint8Array> {
  const wrapper = new UsePdfLib();
  await wrapper.open(existingPdfBytes);
  await wrapper.drawStamp(info, 40);
  const result = await wrapper.close();
  return result;
}
